/**
 * Wave 5 contract status helpers.
 *
 * Backend machine: SENT → PRESENTED → PARTIALLY_SIGNED → CLIENT_SIGNED →
 * EXECUTED (or EXECUTED_PAPER). Legacy DRAFT / PENDING_SIGNATURE remain
 * signable for older rows.
 */

export const SIGNABLE_CONTRACT_STATUSES = [
  'DRAFT',
  'PENDING_SIGNATURE',
  'SENT',
  'PRESENTED',
  'PARTIALLY_SIGNED',
] as const;

export type ContractOutcome = 'active' | 'lost' | 'won' | 'all';

export const CONTRACT_OUTCOMES: ContractOutcome[] = ['active', 'lost', 'won', 'all'];

export const WON_CONTRACT_STATUSES = ['WON', 'EXECUTED', 'EXECUTED_PAPER'] as const;
export const LOST_CONTRACT_STATUSES = ['LOST', 'DECLINED'] as const;
export const ACTIVE_HIDDEN_CONTRACT_STATUSES = [
  'LOST',
  'DECLINED',
  'VOIDED',
  'EXPIRED',
] as const;

export function isSignableContractStatus(status: string): boolean {
  return (SIGNABLE_CONTRACT_STATUSES as readonly string[]).includes(status);
}

/**
 * Portal signing stays open after the first of two clients signs.
 * Older rows flipped to CLIENT_SIGNED too early; those still need Client 2.
 */
export function isClientPortalSignable(
  status: string,
  anotherClientStillNeeded: boolean,
): boolean {
  if (status === 'CLIENT_SIGNED') return false;
  if (isSignableContractStatus(status)) return true;
  return anotherClientStillNeeded && status === 'PARTIALLY_SIGNED';
}

/** Staff company pad: trust CLIENT_SIGNED even if typed names do not match ink. */
export function companyCountersignReady(
  status: string,
  namedClientsDone: boolean,
): boolean {
  return status === 'CLIENT_SIGNED' || status === 'WON' || namedClientsDone;
}

const ESIGN_IN_FLIGHT_STATUSES = [
  'SENT',
  'PRESENTED',
  'PARTIALLY_SIGNED',
  'CLIENT_SIGNED',
  'PENDING_SIGNATURE',
] as const;

export function isWonContractStatus(status: string): boolean {
  return (WON_CONTRACT_STATUSES as readonly string[]).includes(status);
}

export function isLostContractStatus(status: string): boolean {
  return (LOST_CONTRACT_STATUSES as readonly string[]).includes(status);
}

export function isExecutedContractStatus(status: string): boolean {
  return isWonContractStatus(status);
}

/**
 * Financing and down-payment rewrite the contract total. After any
 * client has signed, those controls must freeze so later parties cannot
 * change the figure the first signer agreed to.
 */
export function areContractDecisionsLocked(
  status: string,
  hasClientSignature = false,
): boolean {
  if (isWonContractStatus(status)) return true;
  if (status === 'PARTIALLY_SIGNED' || status === 'CLIENT_SIGNED') return true;
  return hasClientSignature;
}

export function canMarkContractOutcome(status: string): boolean {
  if (isExecutedContractStatus(status) || isLostContractStatus(status)) return false;
  // E-sign in flight: won comes from company countersign, not Mark won.
  return !(ESIGN_IN_FLIGHT_STATUSES as readonly string[]).includes(status);
}

export function outcomeForContractStatus(status: string): Exclude<ContractOutcome, 'all'> {
  if (isWonContractStatus(status)) return 'won';
  if (isLostContractStatus(status) || status === 'VOIDED' || status === 'EXPIRED') {
    return status === 'VOIDED' || status === 'EXPIRED' ? 'lost' : 'lost';
  }
  return 'active';
}

/** Maps a list-page outcome filter to the GET /contracts?outcome= query value. */
export function contractListOutcomeParam(outcome: ContractOutcome): ContractOutcome {
  return CONTRACT_OUTCOMES.includes(outcome) ? outcome : 'active';
}
